import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/modern/components/ui/dialog-shadcn";
import { EntityChecklistProvider, useEntityChecklistContext } from "../../context/EntityChecklistContext";
import { DialogClose } from "@/modern/components/ui/dialog";
import { Loader2, PencilIcon, XIcon } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { Label } from "@/modern/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { ChecklistMenu } from "../ChecklistMenu";
import { ChecklistItemAddForm } from "../ChecklistItemAddForm";
import { ChecklistItemList } from "../ChecklistItemList";
import { ChecklistProgress } from "../ChecklistProgress";
import { Editable, EditableArea, EditableInput, EditablePreview, EditableTrigger } from "@/modern/components/ui/editable";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { checkGrantOrder, cn } from "@/modern/utils";
import { EntityRecord } from "@/types/entity";
import { DateHelper } from "@bryntum/schedulerpro";

// Reusable metadata card component
const MetadataCard = ({ label, value }: { label: string; value: string | React.ReactNode }) => (
  <div className="rounded-lg border border-border/70 bg-muted/20 px-4 py-3">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 font-medium text-foreground">{value || "N/A"}</p>
  </div>
);

interface ChecklistEditorProps {
  record: {
    entity: EntityRecord;
    checklistId?: string | number;
    isCreatingNew?: boolean;
  };
  onClose: () => void;
  editorInstance?: unknown;
}

const ChecklistEditor = ({ onClose }: Pick<ChecklistEditorProps, "onClose">) => {
  const { swivuAppGrants, project } = useSwivuBryntumContext();
  const { selectedChecklist, selectedChecklistItems, isCreatingNewChecklist, updateChecklist, canEditSelectedChecklist, newChecklist, persistNewChecklist } = useEntityChecklistContext();
  const [checklistTitle, setChecklistTitle] = useState("");;
  const checklist = newChecklist || selectedChecklist;

  // Get updater name using project from context
  const updaterName = React.useMemo(() => {
    if (!checklist) return "N/A";
    return project.boardUsersStore?.getById(checklist.updater)?.name || "N/A";
  }, [checklist?.updater, project]);

  useEffect(() => {
    setChecklistTitle(checklist?.title || "");
  }, [checklist])

  return (
    <Dialog
      open={true}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isCreatingNewChecklist) {
          onClose();
        }
      }}
    >
      <DialogContent showCloseButton={false} className="z-[--swivu-bryntum-z-index] sm:max-w-5xl h-[80dvh] flex flex-col p-0 gap-0 focus-visible:outline-none rounded-md">
        <DialogHeader className="flex items-center justify-between flex-row gap-3 space-y-0 p-4 border-b flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Editable
              defaultEditing={Boolean(newChecklist)}
              value={checklistTitle}
              onValueChange={setChecklistTitle}
              onSubmit={(value) => {
                if (!value || !checklist) return;
                updateChecklist(checklist, { title: value });
                if (newChecklist) {
                  persistNewChecklist();
                }
              }}
              placeholder="Enter title..."
              triggerMode="click"
              className="group flex max-w-full min-w-0 flex-1 flex-row items-center gap-0.5"
              disabled={!(canEditSelectedChecklist || checklist?.isPhantom)}
            >
              <EditableArea className="min-w-0 max-w-full data-[editing]:block data-[editing]:w-full data-[editing]:flex-1">
                <EditablePreview
                  className={cn(
                    "min-h-9 truncate border-0 py-2 pl-0 pr-0 text-lg font-semibold",
                    checklist?.archived && "line-through",
                    !(canEditSelectedChecklist || checklist?.isPhantom) && "!min-h-0 !p-0 !leading-none"
                  )}
                />
                <EditableInput
                  className={cn(
                    "h-9 w-full rounded-md border border-input bg-background px-3 text-lg font-semibold",
                    "shadow-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0",
                    checklist?.archived && "line-through"
                  )}
                />
              </EditableArea>
              {(canEditSelectedChecklist || newChecklist) && (
                <EditableTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="inline-flex h-8 w-8 shrink-0 opacity-0 pointer-events-none transition-opacity text-muted-foreground hover:text-foreground group-hover:opacity-100 group-hover:pointer-events-auto"
                  >
                    <PencilIcon className="size-4" />
                  </Button>
                </EditableTrigger>
              )}
            </Editable>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Status Indicator */}
            {isCreatingNewChecklist && (
              <div className="flex items-center gap-2 text-sm px-4 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Saving...
              </div>
            )}
            <ChecklistMenu />
            <DialogClose disabled={isCreatingNewChecklist} asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                <XIcon className="size-6" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* Main Content Area with Embedded Sidebar */}
        <div className="flex-1 flex min-h-0 relative py-6 px-6">
          {/* Main Checklist Content */}
          <div className="flex-1 flex flex-col gap-4 mr-6">
            {checklist && <ChecklistProgress checklist={checklist} checklistItems={selectedChecklistItems} />}

            <div className="flex-1 min-h-0 overflow-y-auto -mx-4 px-4">
              <ChecklistItemList checklistItems={selectedChecklistItems} />
            </div>

            <ChecklistItemAddForm />
          </div>

          {/* Embedded Settings Sidebar */}
          <div className="bg-background overflow-y-auto w-80 opacity-100 border-l">
            {/* Header */}
            <div className="grid gap-y-10 pb-4">
              {/* Permissions Section */}
              <div className="space-y-3 px-4">
                <h4 className="text-xs text-muted-foreground uppercase">Permissions</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="canEdit" className="text-sm font-medium">
                      Can Edit
                    </Label>
                    <Select
                      disabled={isCreatingNewChecklist || Boolean(newChecklist) || !canEditSelectedChecklist}
                      value={String(checklist?.canEdit)}
                      onValueChange={(value) => {
                        if (!checklist) return;
                        const newCanEdit = Number(value);
                        const currentCanView = checklist.canView;

                        // Check if canEdit is higher than canView, and auto-correct canView if needed
                        checkGrantOrder({
                          canEdit: newCanEdit,
                          canView: currentCanView,
                          onViewPermissionError: (canEdit) => {
                            // Auto-correct canView to match canEdit
                            updateChecklist(checklist, { canEdit, canView: canEdit });
                          }
                        });

                        // If validation passed, just update canEdit
                        if (newCanEdit <= currentCanView) {
                          updateChecklist(checklist, { canEdit: newCanEdit });
                        }
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select who can edit" />
                      </SelectTrigger>
                      <SelectContent>
                        {swivuAppGrants.map((grant) => (
                          <SelectItem key={grant.value} value={String(grant.value)} disabled={grant.disabled}>
                            {grant.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="canView" className="text-sm font-medium">
                      Can View
                    </Label>
                    <Select
                      disabled={isCreatingNewChecklist || Boolean(newChecklist) || !canEditSelectedChecklist}
                      value={String(checklist?.canView)}
                      onValueChange={(value) => {
                        if (!checklist) return;
                        const newCanView = Number(value);
                        const currentCanEdit = checklist.canEdit;

                        // Validate that canView is not higher than canEdit
                        const isValid = checkGrantOrder({
                          canEdit: currentCanEdit,
                          canView: newCanView
                        });

                        // Only update if validation passed
                        if (isValid) {
                          updateChecklist(checklist, { canView: newCanView });
                        }
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select who can view" />
                      </SelectTrigger>
                      <SelectContent>
                        {swivuAppGrants.map((grant) => (
                          <SelectItem key={grant.value} value={String(grant.value)} disabled={grant.disabled}>
                            {grant.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Metadata Section */}
              <div className="space-y-3 px-4">
                <h4 className="text-xs text-muted-foreground uppercase">Metadata</h4>
                <div className="grid gap-3 text-sm">
                  <MetadataCard
                    label="Last Updated"
                    value={
                      <>
                        {updaterName}
                        <br />
                        {checklist?.createdAt && DateHelper.format(new Date(checklist.createdAt), "MM/DD/YYYY")}
                      </>
                    }
                  />
                  <MetadataCard
                    label="Created"
                    value={checklist?.createdAt && DateHelper.format(new Date(checklist.createdAt), "MM/DD/YYYY")}
                  />
                  <MetadataCard label="Age" value={String(checklist?.aging)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SwivuChecklistEditorPopup = ({ record, onClose }: ChecklistEditorProps) => {
  const { entity, checklistId, isCreatingNew } = record;

  return (
    <EntityChecklistProvider
      record={entity}
      initialChecklistId={checklistId}
      isCreatingNew={isCreatingNew}
      onCloseEditor={onClose}
    >
      <ChecklistEditor onClose={onClose} />
    </EntityChecklistProvider>
  );
};